import { useState, useEffect, useCallback } from 'react';
import { BASE, authFetch } from '../constants';
import PageLayout from '../components/ui/PageLayout';
import TabBar from '../components/ui/TabBar';
import KpiGrid from '../components/ui/KpiGrid';
import DataTable, { Column } from '../components/ui/DataTable';

const API = BASE;

type Tab = 'ap' | 'ar' | 'fa' | 'inv' | 'recon' | 'uploads';

interface APRow {
  id: number; vendor_code: string; vendor_name: string;
  invoice_number: string; invoice_amount: number; open_amount: number;
  aging_bucket: string; aging_days: number; three_way_match_status: string;
  line_status: string; period: string; currency: string;
}

interface ARRow {
  id: number; customer_code: string; customer_name: string;
  invoice_number: string; invoice_amount: number; open_amount: number;
  aging_bucket: string; aging_days: number; line_status: string;
  credit_limit: number | null; period: string; currency: string;
}

interface FARow {
  id: number; asset_number: string; asset_description: string;
  asset_class: string; asset_status: string; acquisition_cost: number;
  net_book_value: number; depreciation_method: string;
  impairment_indicator: boolean; currency: string; period: string;
  useful_life_years: number | null; ytd_depreciation: number;
}

interface INVRow {
  id: number; item_code: string; item_description: string;
  item_category: string; warehouse_location: string;
  quantity_on_hand: number; unit_cost: number; total_value: number;
  valuation_method: string; days_since_movement: number;
  slow_moving_flag: boolean; obsolescence_flag: boolean;
  currency: string; period: string;
}

interface Upload {
  id: number; subledger_type: string; period: string; source_file: string;
  status: string; row_count: number | null; created_at: string;
  entity_id: number; source_system: string | null;
}

interface Summary {
  total_open: number; aging_summary: Record<string, number>;
  [key: string]: any;
}

const BUCKETS = ['CURRENT', '30', '60', '90', '120PLUS'];

const bucketLabel = (b: string) =>
  b === 'CURRENT' ? 'Current' : b === '120PLUS' ? '120+' : `${b} Days`;

const fmtCurrency = (n: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);

export default function SubledgerView() {
  const [tab, setTab] = useState<Tab>('ap');
  const [entityId] = useState(1000);
  const [period] = useState('2024-12');

  // AP state
  const [apRows, setApRows] = useState<APRow[]>([]);
  const [apTotal, setApTotal] = useState(0);
  const [apSummary, setApSummary] = useState<Summary | null>(null);

  // AR state
  const [arRows, setArRows] = useState<ARRow[]>([]);
  const [arTotal, setArTotal] = useState(0);
  const [arSummary, setArSummary] = useState<Summary | null>(null);

  // FA state
  const [faRows, setFaRows] = useState<FARow[]>([]);
  const [faTotal, setFaTotal] = useState(0);
  const [faSummary, setFaSummary] = useState<any>(null);

  // INV state
  const [invRows, setInvRows] = useState<INVRow[]>([]);
  const [invTotal, setInvTotal] = useState(0);
  const [invSummary, setInvSummary] = useState<any>(null);

  // Uploads state
  const [uploads, setUploads] = useState<Upload[]>([]);
  const [uploadsTotal, setUploadsTotal] = useState(0);

  const [loading, setLoading] = useState(false);

  const fetchAP = useCallback(async () => {
    setLoading(true);
    try {
      const [rowsRes, sumRes] = await Promise.all([
        authFetch(`${API}/api/v1/subledger/ap?entity_id=${entityId}&period=${period}&per_page=100`),
        authFetch(`${API}/api/v1/subledger/ap/summary?entity_id=${entityId}&period=${period}`),
      ]);
      if (rowsRes.ok) { const d = await rowsRes.json(); setApRows(d.items || []); setApTotal(d.total || 0); }
      if (sumRes.ok) { setApSummary(await sumRes.json()); }
    } catch { /* ignore */ }
    setLoading(false);
  }, [entityId, period]);

  const fetchAR = useCallback(async () => {
    setLoading(true);
    try {
      const [rowsRes, sumRes] = await Promise.all([
        authFetch(`${API}/api/v1/subledger/ar?entity_id=${entityId}&period=${period}&per_page=100`),
        authFetch(`${API}/api/v1/subledger/ar/summary?entity_id=${entityId}&period=${period}`),
      ]);
      if (rowsRes.ok) { const d = await rowsRes.json(); setArRows(d.items || []); setArTotal(d.total || 0); }
      if (sumRes.ok) { setArSummary(await sumRes.json()); }
    } catch { /* ignore */ }
    setLoading(false);
  }, [entityId, period]);

  const fetchFA = useCallback(async () => {
    setLoading(true);
    try {
      const [rowsRes, sumRes] = await Promise.all([
        authFetch(`${API}/api/v1/subledger/fa?entity_id=${entityId}&period=${period}&per_page=100`),
        authFetch(`${API}/api/v1/subledger/fa/summary?entity_id=${entityId}&period=${period}`),
      ]);
      if (rowsRes.ok) { const d = await rowsRes.json(); setFaRows(d.items || []); setFaTotal(d.total || 0); }
      if (sumRes.ok) { setFaSummary(await sumRes.json()); }
    } catch { /* ignore */ }
    setLoading(false);
  }, [entityId, period]);

  const fetchINV = useCallback(async () => {
    setLoading(true);
    try {
      const [rowsRes, sumRes] = await Promise.all([
        authFetch(`${API}/api/v1/subledger/inv?entity_id=${entityId}&period=${period}&per_page=100`),
        authFetch(`${API}/api/v1/subledger/inv/summary?entity_id=${entityId}&period=${period}`),
      ]);
      if (rowsRes.ok) { const d = await rowsRes.json(); setInvRows(d.items || []); setInvTotal(d.total || 0); }
      if (sumRes.ok) { setInvSummary(await sumRes.json()); }
    } catch { /* ignore */ }
    setLoading(false);
  }, [entityId, period]);

  const fetchUploads = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${API}/api/v1/subledger/uploads?per_page=100`);
      if (res.ok) { const d = await res.json(); setUploads(d.uploads || []); setUploadsTotal(d.total || 0); }
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (tab === 'ap') fetchAP();
    else if (tab === 'ar') fetchAR();
    else if (tab === 'fa') fetchFA();
    else if (tab === 'inv') fetchINV();
    else if (tab === 'uploads') fetchUploads();
  }, [tab, fetchAP, fetchAR, fetchFA, fetchINV, fetchUploads]);

  const renderAgingSummary = (summary: Summary | null) => {
    if (!summary) return null;
    const aging = summary.aging_summary || {};
    const items = BUCKETS.map(b => ({
      label: bucketLabel(b),
      value: fmtCurrency(aging[b] || 0),
    }));
    items.push({ label: 'Total Open', value: fmtCurrency(summary.total_open || 0), });
    return <KpiGrid items={items} />;
  };

  // AP columns
  const apColumns: Column[] = [
    { key: 'vendor_code', label: 'Vendor' },
    { key: 'invoice_number', label: 'Invoice' },
    { key: 'invoice_amount', label: 'Amount', align: 'right', render: (v: number) => fmtCurrency(v) },
    { key: 'open_amount', label: 'Open', align: 'right', render: (v: number) => fmtCurrency(v) },
    { key: 'aging_bucket', label: 'Aging', render: (v: string) => bucketLabel(v) },
    { key: 'aging_days', label: 'Days', align: 'right' },
    { key: 'three_way_match_status', label: 'Match' },
    { key: 'line_status', label: 'Status' },
  ];

  // AR columns
  const arColumns: Column[] = [
    { key: 'customer_code', label: 'Customer' },
    { key: 'invoice_number', label: 'Invoice' },
    { key: 'invoice_amount', label: 'Amount', align: 'right', render: (v: number) => fmtCurrency(v) },
    { key: 'open_amount', label: 'Open', align: 'right', render: (v: number) => fmtCurrency(v) },
    { key: 'aging_bucket', label: 'Aging', render: (v: string) => bucketLabel(v) },
    { key: 'aging_days', label: 'Days', align: 'right' },
    { key: 'credit_limit', label: 'Credit Limit', align: 'right', render: (v: number | null) => v != null ? fmtCurrency(v) : '-' },
    { key: 'line_status', label: 'Status' },
  ];

  // FA columns
  const faColumns: Column[] = [
    { key: 'asset_number', label: 'Asset #' },
    { key: 'asset_description', label: 'Description', width: 200 },
    { key: 'asset_class', label: 'Class' },
    { key: 'asset_status', label: 'Status' },
    { key: 'acquisition_cost', label: 'Cost', align: 'right', render: (v: number) => fmtCurrency(v) },
    { key: 'net_book_value', label: 'NBV', align: 'right', render: (v: number) => fmtCurrency(v) },
    { key: 'depreciation_method', label: 'Method' },
    { key: 'ytd_depreciation', label: 'YTD Depr', align: 'right', render: (v: number) => fmtCurrency(v) },
    { key: 'impairment_indicator', label: 'Impaired', render: (v: boolean) => v ? 'Yes' : '-' },
  ];

  // INV columns
  const invColumns: Column[] = [
    { key: 'item_code', label: 'Item' },
    { key: 'item_description', label: 'Description', width: 200 },
    { key: 'item_category', label: 'Category' },
    { key: 'warehouse_location', label: 'Warehouse' },
    { key: 'quantity_on_hand', label: 'Qty', align: 'right' },
    { key: 'unit_cost', label: 'Cost', align: 'right', render: (v: number) => fmtCurrency(v) },
    { key: 'total_value', label: 'Value', align: 'right', render: (v: number) => fmtCurrency(v) },
    { key: 'valuation_method', label: 'Method' },
    { key: 'days_since_movement', label: 'Days Idle', align: 'right' },
    { key: 'flags', label: 'Flags', render: (_: any, row: INVRow) => {
      if (row.obsolescence_flag) return <span style={{ color: 'var(--negative)' }}>OBS</span>;
      if (row.slow_moving_flag) return <span style={{ color: 'var(--warning)' }}>SLOW</span>;
      return '-';
    }},
  ];

  // Upload columns
  const uploadColumns: Column[] = [
    { key: 'id', label: 'ID' },
    { key: 'subledger_type', label: 'Type' },
    { key: 'entity_id', label: 'Entity' },
    { key: 'period', label: 'Period' },
    { key: 'source_file', label: 'File' },
    { key: 'source_system', label: 'System', render: (v: string | null) => v || '-' },
    { key: 'row_count', label: 'Rows', align: 'right', render: (v: number | null) => v ?? '-' },
    { key: 'status', label: 'Status', render: (v: string) => {
      const colors: Record<string, string> = { COMPLETED: 'var(--positive)', FAILED: 'var(--negative)', PROCESSING: 'var(--navy-800)', PENDING: 'var(--warning)', PARTIAL: 'var(--warning)' };
      return <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, background: `${colors[v] || 'var(--text-hint)'}15`, color: colors[v] || 'var(--text-hint)' }}>{v}</span>;
    }},
    { key: 'created_at', label: 'Uploaded', render: (v: string) => <span style={{ fontSize: 11 }}>{v ? new Date(v).toLocaleString() : '-'}</span> },
  ];

  return (
    <PageLayout title="Sub-ledger Management">
      <TabBar
        tabs={[
          { key: 'ap', label: 'Accounts Payable' },
          { key: 'ar', label: 'Accounts Receivable' },
          { key: 'fa', label: 'Fixed Assets' },
          { key: 'inv', label: 'Inventory' },
          { key: 'recon', label: 'Reconciliation' },
          { key: 'uploads', label: 'Upload History' },
        ]}
        active={tab}
        onChange={t => setTab(t as Tab)}
      />

      {loading && <div style={{ color: 'var(--text-hint)', marginBottom: 12, fontSize: 13 }}>Loading...</div>}

      {/* AP Tab */}
      {tab === 'ap' && (
        <div>
          {renderAgingSummary(apSummary)}
          {apSummary && apSummary.match_summary && (
            <KpiGrid items={Object.entries(apSummary.match_summary as Record<string, number>).map(([k, v]) => ({ label: k, value: v }))} />
          )}
          <div style={{ fontSize: 13, color: 'var(--text-hint)', marginBottom: 8 }}>{apTotal} AP lines</div>
          <DataTable columns={apColumns} rows={apRows} rowKey="id" loading={loading} emptyMessage="No AP data. Upload a sub-ledger file to get started." />
        </div>
      )}

      {/* AR Tab */}
      {tab === 'ar' && (
        <div>
          {renderAgingSummary(arSummary)}
          {arSummary && (
            <KpiGrid items={[
              ...(arSummary.dso_estimate != null ? [{ label: 'DSO Estimate', value: `${arSummary.dso_estimate} days` }] : []),
              ...(arSummary.credit_breaches != null ? [{ label: 'Credit Breaches', value: arSummary.credit_breaches, color: arSummary.credit_breaches > 0 ? 'var(--negative)' : 'var(--positive)' }] : []),
            ]} />
          )}
          <div style={{ fontSize: 13, color: 'var(--text-hint)', marginBottom: 8 }}>{arTotal} AR lines</div>
          <DataTable columns={arColumns} rows={arRows} rowKey="id" loading={loading} emptyMessage="No AR data. Upload a sub-ledger file to get started." />
        </div>
      )}

      {/* FA Tab */}
      {tab === 'fa' && (
        <div>
          {faSummary && (
            <KpiGrid items={[
              { label: 'Total Assets', value: faSummary.total_assets },
              { label: 'Acquisition Cost', value: fmtCurrency(faSummary.total_acquisition_cost || 0) },
              { label: 'Net Book Value', value: fmtCurrency(faSummary.total_net_book_value || 0), color: 'var(--navy-800)' },
              { label: 'CIP Items', value: faSummary.cip_count || 0, color: 'var(--warning)' },
              { label: 'Impairment', value: faSummary.impairment_candidates || 0, color: faSummary.impairment_candidates > 0 ? 'var(--negative)' : 'var(--text-primary)' },
            ]} />
          )}
          <div style={{ fontSize: 13, color: 'var(--text-hint)', marginBottom: 8 }}>{faTotal} fixed asset records</div>
          <DataTable columns={faColumns} rows={faRows} rowKey="id" loading={loading} emptyMessage="No fixed asset data. Upload a sub-ledger file to get started." />
        </div>
      )}

      {/* INV Tab */}
      {tab === 'inv' && (
        <div>
          {invSummary && (
            <KpiGrid items={[
              { label: 'Unique Items', value: invSummary.unique_items },
              { label: 'Total Value', value: fmtCurrency(invSummary.total_value || 0), color: 'var(--navy-800)' },
              { label: 'Slow Moving', value: invSummary.slow_moving_count || 0, color: invSummary.slow_moving_count > 0 ? 'var(--warning)' : 'var(--text-primary)' },
              { label: 'Obsolete', value: invSummary.obsolete_count || 0, color: invSummary.obsolete_count > 0 ? 'var(--negative)' : 'var(--text-primary)' },
              { label: 'Negative Qty', value: invSummary.negative_qty_count || 0, color: invSummary.negative_qty_count > 0 ? 'var(--negative)' : 'var(--text-primary)' },
            ]} />
          )}
          <div style={{ fontSize: 13, color: 'var(--text-hint)', marginBottom: 8 }}>{invTotal} inventory records</div>
          <DataTable columns={invColumns} rows={invRows} rowKey="id" loading={loading} emptyMessage="No inventory data. Upload a sub-ledger file to get started." />
        </div>
      )}

      {/* Reconciliation Tab */}
      {tab === 'recon' && (
        <div style={{ background: 'var(--bg-card)', borderRadius: 8, padding: 20, maxWidth: 500, border: '1px solid var(--border-subtle)' }}>
          <h3 style={{ margin: '0 0 16px', color: 'var(--navy-900)', fontSize: 16, fontWeight: 700 }}>GL Reconciliation</h3>
          <p style={{ fontSize: 13, lineHeight: 1.6, margin: '0 0 16px', color: 'var(--text-secondary)' }}>
            Compare sub-ledger totals against GL control account balances.
            Variances beyond tolerance automatically create exceptions.
          </p>
          <p style={{ fontSize: 12, color: 'var(--text-hint)' }}>
            Use the API endpoint POST /api/v1/subledger/reconcile or upload sub-ledger data first.
          </p>
        </div>
      )}

      {/* Uploads Tab */}
      {tab === 'uploads' && (
        <div>
          <div style={{ fontSize: 13, color: 'var(--text-hint)', marginBottom: 8 }}>{uploadsTotal} uploads</div>
          <DataTable columns={uploadColumns} rows={uploads} rowKey="id" loading={loading} emptyMessage="No uploads yet." />
        </div>
      )}
    </PageLayout>
  );
}
